import React, { useMemo, useState } from 'react';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { Cron, CronType, SetValueFunctionExtra, PeriodType } from 'react-js-cron';
import parser from 'cron-parser';
import 'react-js-cron/dist/styles.css';

import cn from 'components/UserSettings/HRIS/common/ScheduleControl/styles.module.scss';
import classNames from 'classnames';

type ScheduleControlProps = {
  schedule?: string,
  onChange: (value: string) => void,
  error?: string,
}

export const mapCron = (value: string) => {
  const [min, hour, dayOfMonth, month, dayOfWeek] = value.split(' ');

  const adjustedDayOfWeek = dayOfWeek !== '*' && dayOfWeek !== '?' ? parseInt(dayOfWeek) + 1 : dayOfWeek;

  if (dayOfMonth !== '*') {
    return `${min} ${hour} ${dayOfMonth} ${month} ?`
  }

  if (dayOfWeek !== '*') {
    return `${min} ${hour} ? ${month} ${adjustedDayOfWeek}`
  }

  return `${min} ${hour} ${dayOfMonth} ${month} ${adjustedDayOfWeek}`;
}

export const parseCron = (value: string) => {
  if (!value) {
    return '';
  }
  const cleanedValue = value.replace('?', '*');
  const [min, hour, dayOfMonth, month, dayOfWeek] = cleanedValue.split(' ');

  const adjustedDayOfWeek = dayOfWeek !== '*' && dayOfWeek !== '?' ? parseInt(dayOfWeek) - 1 : dayOfWeek;

  return `${min} ${hour} ${dayOfMonth} ${month} ${adjustedDayOfWeek}`;
}

export const isScheduleValid = (schedule: string) => {
  const cron = parseCron(schedule);
  const interval = parser.parseExpression(cron);
  const fields = JSON.parse(JSON.stringify(interval.fields));
  return (
    fields.dayOfWeek.length === 1
      || fields.dayOfMonth.length === 1
      || (fields.month.length === 1 && fields.dayOfMonth.length === 1)
    )
    && fields.hour.length === 1
    && fields.minute.length === 1;
}

export const isUrlValid = (value: string) => {
  const url = new URL(value);
  const format = url?.searchParams?.get('format');

  return format === 'json' || format === 'csv';
}

export const ScheduleControl = ({ schedule = '', onChange, error }: ScheduleControlProps) => {
  const [period, setPeriod] = useState<PeriodType>('week');

  const parsedValue = useMemo(() => {
    return parseCron(schedule);
  }, [schedule])

  const handleChange = (value: string, { selectedPeriod }: SetValueFunctionExtra) => {
    const mappedCron = mapCron(value);
    if (mappedCron) {
      onChange(mappedCron);
    }
    setPeriod(selectedPeriod);
  }

  const allowedDropdowns: CronType[] = useMemo(() => {
    if (period === 'year') {
      return ['period', 'months', 'month-days', 'hours', 'minutes'];
    }

    if (period === 'month') {
      return ['period', 'month-days', 'hours', 'minutes'];
    }

    return ['period', 'months', 'week-days', 'month-days', 'hours', 'minutes']
  }, [period]);

  return (
    <div>
      <div className={cn.label}>
        Schedule
      </div>
      <div className={error ? cn.errorControl : undefined}>
        <Cron
          value={parsedValue}
          setValue={handleChange}
          className={classNames(cn.control)}
          mode="single"
          allowEmpty="never"
          allowedDropdowns={allowedDropdowns}
          allowedPeriods={['year', 'month', 'week']}
          defaultPeriod="week"
          clearButton={false}
          displayError={false}
          locale={{
            everyText: 'Select',
            emptyMonthDays: 'Select a day of the month',
            emptyWeekDays: 'Select a day of the week',
            emptyHours: 'Select an hour',
            emptyMinutes: 'Select a minute',
            emptyMonths: 'Select a month',
          }}
        />
      </div>
      {!!error && (
        <div className={cn.errorContainer}>
          <ExclamationCircleFilled className={cn.icon} />
          {error}
        </div>
      )}
    </div>
  )
}
